import React, { useEffect } from 'react';
import CubemateGuide from './docs/CubemateGuide';
import BookmateGuide from './docs/BookmateGuide';
import WorkflowGuide from './docs/WorkflowGuide';
import CapstoneGuide from './docs/CapstoneGuide';
import './DocumentationModal.css';

interface DocumentationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onPromptClick: (prompt: string) => void;
  currentRoute: 'chat' | 'bookmate' | 'flowmate' | 'capstone';
}

const DocumentationModal: React.FC<DocumentationModalProps> = ({
  isOpen,
  onClose,
  onPromptClick,
  currentRoute
}) => {
  // Handle ESC key to close modal
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEsc);
      // Prevent body scroll when modal is open
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEsc);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const handlePromptClick = (prompt: string) => {
    onPromptClick(prompt);
    onClose(); // Auto-close modal after clicking prompt
  };

  const renderGuide = () => {
    switch (currentRoute) {
      case 'chat':
        return <CubemateGuide onPromptClick={handlePromptClick} />;
      case 'bookmate':
        return <BookmateGuide onPromptClick={handlePromptClick} />;
      case 'flowmate':
        return <WorkflowGuide onPromptClick={handlePromptClick} />;
      case 'capstone':
        return <CapstoneGuide onPromptClick={handlePromptClick} />;
      default:
        return <CubemateGuide onPromptClick={handlePromptClick} />;
    }
  };

  return (
    <div className="documentation-modal-overlay" onClick={onClose}>
      <div
        className="documentation-modal-content"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          className="documentation-modal-close"
          onClick={onClose}
          aria-label="Close documentation"
        >
          ×
        </button>
        <div className="documentation-modal-body">
          {renderGuide()}
        </div>
      </div>
    </div>
  );
};

export default DocumentationModal;
